import { useTranslation } from '@/hooks/useTranslation';
import { CurrencyDisplay } from './CurrencyDisplay';
import { ArrowDownToLine, Flag, RefreshCcw } from 'lucide-react';
import { Badge } from '@ophelos/ui/components/badge';

interface PaymentPlanProgressProps {
  totalPayments: number;
  completedPayments: number;
  originalAmount?: number;
  /** Payments received, in currency units — not the balance delta, which adjustments also move. */
  amountPaid?: number;
  /** The next instalment due, in currency units. */
  instalmentAmount?: number;
  currency?: string;
}

export function PaymentPlanProgress({
  totalPayments,
  completedPayments,
  originalAmount,
  amountPaid,
  instalmentAmount,
  currency = 'EUR',
}: PaymentPlanProgressProps) {
  const { t } = useTranslation();
  const safeTotal = Math.max(1, totalPayments);
  const progress = (completedPayments / safeTotal) * 100;

  // Calculate gradient color for each completed payment (orange -> amber -> green transition)
  const getPaymentStyle = (paymentIndex: number) => {
    if (paymentIndex >= completedPayments) {
      return { className: 'bg-muted-foreground/20' };
    }

    // Calculate position in gradient (0 = start/orange, 1 = end/green)
    const gradientProgress = totalPayments > 1 ? paymentIndex / (totalPayments - 1) : 1;

    let r: number, g: number, b: number;

    if (gradientProgress < 0.5) {
      // First half: Orange -> Amber
      // Orange: rgb(249, 115, 22) -> Amber: rgb(251, 191, 36)
      const t = gradientProgress * 2; // 0 to 1 for first half
      r = Math.round(249 + (251 - 249) * t);
      g = Math.round(115 + (191 - 115) * t);
      b = Math.round(22 + (36 - 22) * t);
    } else {
      // Second half: Amber -> Green
      // Amber: rgb(251, 191, 36) -> Green: rgb(34, 197, 94)
      const t = (gradientProgress - 0.5) * 2; // 0 to 1 for second half
      r = Math.round(251 - (251 - 34) * t);
      g = Math.round(191 + (197 - 191) * t);
      b = Math.round(36 + (94 - 36) * t);
    }

    return {
      style: { backgroundColor: `rgb(${r}, ${g}, ${b})` },
      className: '',
    };
  };

  return (
    <div className="space-y-2 mt-4">
      {/* Progress segments */}

      <div className="flex gap-0.5">
        {Array.from({ length: totalPayments }, (_, i) => {
          const { style, className } = getPaymentStyle(i);
          return <div key={i} className={`h-1.5 flex-1 rounded-md transition-all duration-300 ${className}`} style={style} />;
        })}
      </div>

      {/* Amount info (optional) */}
      {originalAmount !== undefined && (
        <div className="stack space-y-2 mt-6">
          <div className="flex items-start justify-between text-sm font-medium border-b pb-2 border-border-light">
            <div className="flex items-center gap-2 text-left">
              <Flag className="size-3.5 text-soft-foreground" />
              <span className="text-muted-foreground">{t('paymentPlanProgress.startingBalance')}:</span>
            </div>

            <CurrencyDisplay amount={originalAmount} currency={currency} className="text-foreground font-semibold" />
          </div>
          {amountPaid !== undefined && (
            <div className="flex items-start justify-between text-sm font-medium border-b pb-2 border-border-light">
              <div className="flex items-center gap-2 text-left">
                <ArrowDownToLine className="size-3.5 text-soft-foreground" />
                <span className="text-muted-foreground">{t('paymentPlanProgress.amountPaid')}:</span>
              </div>
              <div className="flex items-center gap-2">
                <CurrencyDisplay amount={amountPaid} currency={currency} className="text-foreground font-semibold" />
                <Badge variant="subtle">{Math.round(progress).toFixed(0)}%</Badge>
              </div>
            </div>
          )}
          {instalmentAmount !== undefined && instalmentAmount > 0 && (
            <div className="flex items-start justify-between text-sm font-medium ">
              <div className="flex items-center gap-2 text-left">
                <RefreshCcw className="size-3.5 text-soft-foreground" />
                <span className="text-muted-foreground">{t('paymentPlan.youllPay')}:</span>
              </div>
              <CurrencyDisplay amount={instalmentAmount} currency={currency} className="text-foreground font-semibold" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
